// Email validation using RFC 5322 standard
export const validateEmail = (email: string) => {
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  return emailRegex.test(email);
};

// Phone validation supporting multiple formats
export const validatePhone = (phone: string) => {
  // Remove all non-numeric characters
  const cleanPhone = phone.replace(/\D/g, '');
  
  // Check if it's a valid length (10 digits for US numbers)
  if (cleanPhone.length !== 10) {
    return false;
  }

  // Ensure it doesn't start with 0 or 1
  if (cleanPhone.startsWith('0') || cleanPhone.startsWith('1')) {
    return false;
  }

  return true;
};

// Format phone number to (XXX) XXX-XXXX
export const formatPhone = (phone: string) => {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length !== 10) return phone;
  
  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
};

// Credit card validation
export const containsCreditCardNumber = (text: string): boolean => {
  // Remove spaces, dashes, and dots
  const cleaned = text.replace(/[\s\-\.]/g, '');

  // Common credit card patterns
  const patterns = [
    // Visa
    /\b4[0-9]{12}(?:[0-9]{3})?\b/,
    // Mastercard
    /\b(?:5[1-5][0-9]{2}|222[1-9]|22[3-9][0-9]|2[3-6][0-9]{2}|27[01][0-9]|2720)[0-9]{12}\b/,
    // American Express
    /\b3[47][0-9]{13}\b/,
    // Discover
    /\b6(?:011|5[0-9]{2})[0-9]{12}\b/,
    // JCB
    /\b(?:2131|1800|35\d{3})\d{11}\b/,
    // Diners Club
    /\b3(?:0[0-5]|[68][0-9])[0-9]{11}\b/,
    // Any 13-19 digit number that could be a card
    /\b[0-9]{13,19}\b/,
    // Numbers with common separators
    /\b[0-9]{4}[\s\-]?[0-9]{4}[\s\-]?[0-9]{4}[\s\-]?[0-9]{4}\b/,
    // Numbers with expiry date pattern
    /\b[0-9]{13,19}\s+(?:0[1-9]|1[0-2])\/[0-9]{2}(?:\s+[0-9]{3,4})?\b/,
    // Numbers with CVV pattern
    /\b[0-9]{13,19}\s+(?:[0-9]{3,4})\b/,
    // Common keywords with numbers
    /\b(?:card|cc|cvv|cvc|ccv|exp|expiry|expiration|pin)\s*:?\s*[0-9]+/i,
    // Numbers with forward slashes (expiry dates)
    /\b[0-9]{13,19}\/[0-9]+\b/,
  ];

  // Check each pattern
  for (const pattern of patterns) {
    if (pattern.test(cleaned)) {
      return true;
    }

    // Also check the original text for patterns with separators
    if (pattern.test(text)) {
      return true;
    }
  }

  // Find sequences of digits and validate with Luhn algorithm
  const numberSequences = cleaned.match(/\d+/g) || [];
  for (const sequence of numberSequences) {
    // Only check sequences that could be credit card numbers (13-19 digits)
    if (sequence.length >= 13 && sequence.length <= 19) {
      if (validateLuhn(sequence)) {
        return true;
      }
    }
  }

  return false;
};

// Luhn algorithm for credit card validation
const validateLuhn = (digits: string): boolean => {
  let sum = 0;
  let isEven = false;

  // Loop through values starting from the rightmost
  for (let i = digits.length - 1; i >= 0; i--) {
    let num = parseInt(digits[i], 10);

    if (isEven) {
      num *= 2;
      if (num > 9) {
        num -= 9;
      }
    }

    sum += num;
    isEven = !isEven;
  }

  return sum % 10 === 0;
};